import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaHandshake } from 'react-icons/fa';
import ModalLayout from '../layouts/ModalLayout';
import ModalHeader from '../misc/ModalHeader';
import CancelSaveButton from '../misc/CancelSaveButton';
import Input from '../misc/Input';
import Labeled from '../misc/Labeled';
import ImageInput from '../forms/ImageInput';
import SponsorCampaignTypePicker from '../misc/SponsorCampaignTypePicker';
import sponsorService from '../../../services/business/sponsorService';
import Sponsor, { SponsorCampaignType } from '../../../types/sponsor';

interface AddEditSponsorModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sponsor?: Sponsor;
  onSaved: (sponsor: Sponsor) => void;
}

export default function AddEditSponsorModal({
  open,
  setOpen,
  sponsor,
  onSaved,
}: AddEditSponsorModalProps): JSX.Element {
  const { t } = useTranslation();
  const isEdit = !!sponsor;

  const [name, setName] = useState('');
  const [campaignType, setCampaignType] = useState<SponsorCampaignType>('COMMUNITY');
  const [url, setUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | undefined>(undefined);
  const [logoFile, setLogoFile] = useState<File | undefined>(undefined);
  const [logoAction, setLogoAction] = useState<'UPLOAD' | 'DELETE' | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(sponsor?.name ?? '');
    setCampaignType(sponsor?.campaignType ?? 'COMMUNITY');
    setUrl(sponsor?.url ?? '');
    setLogoPreview(sponsor?.logo ?? undefined);
    setLogoFile(undefined);
    setLogoAction(undefined);
  }, [open, sponsor]);

  const handleSetImage = async (image: string | undefined): Promise<void> => {
    setLogoPreview(image);
    if (!image) {
      setLogoFile(undefined);
      return;
    }
    const blob = await fetch(image).then((res) => res.blob());
    setLogoFile(new File([blob], 'logo', { type: blob.type }));
  };

  const handleSave = async (): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error(t('component.modal.sponsor.nameRequired'));
      return;
    }

    let trimmedUrl = url.trim();
    if (trimmedUrl && !trimmedUrl.startsWith('http')) {
      trimmedUrl = `https://${trimmedUrl}`;
    }

    setSaving(true);
    try {
      const saved = isEdit
        ? await sponsorService.updateSponsor(sponsor!.sponsorId, {
            name: trimmedName,
            url: trimmedUrl,
            logo: logoAction === 'UPLOAD' ? logoFile : undefined,
          })
        : await sponsorService.createSponsor(
            trimmedName,
            trimmedUrl || undefined,
            logoFile,
            undefined,
            campaignType,
          );

      toast.success(t(isEdit ? 'component.modal.sponsor.updated' : 'component.modal.sponsor.created'));
      onSaved(saved);
      setOpen(false);
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 flex max-h-[90vh] w-fit min-w-[420px] max-w-[560px] flex-col gap-5 overflow-y-auto rounded-[12px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
      <ModalHeader
        icon={FaHandshake}
        title={t(isEdit ? 'component.modal.sponsor.editTitle' : 'component.modal.sponsor.addTitle')}
        subtitle={t('component.modal.sponsor.subtitle')}
      />

      {!isEdit && (
        <SponsorCampaignTypePicker value={campaignType} onChange={setCampaignType} />
      )}

      <div className="flex flex-col gap-4">
        <Input
          id="sponsor-name"
          type="text"
          label={t('component.modal.sponsor.name')}
          placeholder={t('component.modal.sponsor.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Labeled label={t('component.modal.sponsor.logo')}>
          <ImageInput
            className="h-28 w-48"
            iconClassName="w-8 h-8"
            fit="contain"
            image={logoPreview}
            setImage={handleSetImage}
            setImageAction={setLogoAction}
          />
          <p className="text-sm text-gray-500">{t('component.modal.sponsor.logoHint')}</p>
        </Labeled>
        <Input
          id="sponsor-url"
          type="text"
          label={t('component.modal.sponsor.url')}
          placeholder={t('component.modal.sponsor.urlPlaceholder')}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>

      <p className="text-sm text-gray-500">{t('component.modal.sponsor.createHint')}</p>

      <div className="flex w-full justify-end">
        <CancelSaveButton
          onCancel={() => setOpen(false)}
          onSave={handleSave}
          disabled={!name.trim()}
          loading={saving}
        />
      </div>
    </ModalLayout>
  );
}
